import { useEffect, useRef, type ReactNode } from "react";
import { useAuth } from "@clerk/react";

import { attributePendingReferralToken } from "../lib/referral-attribution-api";
import { loadOnboardingSession } from "../lib/onboarding";

/**
 * Runs only after RequireCompletedOnboarding has verified the backend buyer
 * record. It gives buyer-brag and marketplace share tokens one safe
 * first-touch attempt without blocking the destination or surfacing service
 * policy (unknown/revoked/ownerless/self all look identical to this client).
 */
export function ReferralAttributionBridge({
  children,
}: {
  children: ReactNode;
}) {
  const { getToken } = useAuth();
  const attemptedToken = useRef<string | null>(null);

  useEffect(() => {
    const token = loadOnboardingSession()?.referralAttributionToken;
    if (!token || attemptedToken.current === token) return;

    let cancelled = false;
    attemptedToken.current = token;
    void attributePendingReferralToken(getToken)
      .catch(() => {
        // Leave the handoff in session storage for a later authenticated
        // navigation; attribution must never block onboarding or a claim.
        if (!cancelled) attemptedToken.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return <>{children}</>;
}
